/**
 * Detección de fichas duplicadas por NIF en los maestros (contabilidades
 * heredadas). Por defecto se propone conservar la subcuenta más alta, que
 * suele ser la de alta más reciente.
 */
import { normalizeNif } from "@/lib/customerMaster";

export interface MasterEntryLite {
  id: string;
  taxId: string;
  account: string;
  description: string;
}

export interface DuplicateGroup {
  nif: string;
  entries: MasterEntryLite[];
  /** id propuesto para conservar. */
  keepId: string;
}

const compareAccounts = (a: string, b: string) => {
  const na = Number(a.replace(/\D/g, "")), nb = Number(b.replace(/\D/g, ""));
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  return a.localeCompare(b);
};

export const findDuplicateGroups = (entries: MasterEntryLite[]): DuplicateGroup[] => {
  const byNif = new Map<string, MasterEntryLite[]>();
  for (const e of entries) {
    const nif = normalizeNif(e.taxId || "");
    if (!nif) continue;
    const list = byNif.get(nif);
    if (list) list.push(e);
    else byNif.set(nif, [e]);
  }
  const groups: DuplicateGroup[] = [];
  for (const [nif, list] of byNif) {
    if (list.length < 2) continue;
    const sorted = [...list].sort((a, b) => compareAccounts(b.account, a.account));
    groups.push({ nif, entries: sorted, keepId: sorted[0].id });
  }
  return groups.sort((a, b) => a.entries[0].description.localeCompare(b.entries[0].description));
};

/** Ids a borrar según la ficha elegida para conservar en cada grupo. */
export const idsToDelete = (groups: DuplicateGroup[], keep: Record<string, string>): string[] =>
  groups.flatMap((g) => {
    const k = keep[g.nif] ?? g.keepId;
    return g.entries.filter((e) => e.id !== k).map((e) => e.id);
  });
